import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import {
  TrendingUp as TrendingUpIcon,
  BarChart as BarChartIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { Logger } from 'utils/Logger';
import { handleApiError } from 'utils/ErrorHandler';
import { apiClient } from 'utils/ApiClient';
import { CAMPAIGN_RUNNER_ANALYTICS_URL } from 'envConfig';

interface AnalyticsData {
  timeRange: string;
  totalCampaigns: number;
  totalMessages: number;
  statusBreakdown: {
    completed: number;
    stopped: number;
    cancelled: number;
    running: number;
    scheduled: number;
  };
  timeSeriesData: Array<{
    date: string;
    messages: number;
    completed: number;
    stopped: number;
    cancelled: number;
  }>;
}

type ChartType = 'line' | 'bar' | 'area';
type TimeRange = '7d' | '30d' | '90d';

export const CampaignAnalyticsGraph: React.FC = () => {
  const theme = useTheme();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartType, setChartType] = useState<ChartType>('line');
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');

  const fetchAnalyticsData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await apiClient.get(`/iu/campaignrunner/analytics?timeRange=${timeRange}`);
      setAnalyticsData(data.data);
      Logger.debug('CampaignAnalyticsGraph.tsx', `Analytics data loaded: ${data.data.totalCampaigns} campaigns`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      Logger.error('CampaignAnalyticsGraph.tsx', `Failed to fetch analytics data: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  const handleChartTypeChange = (_event: React.MouseEvent<HTMLElement>, newChartType: ChartType | null) => {
    if (newChartType !== null) {
      setChartType(newChartType);
    }
  };

  const handleTimeRangeChange = (event: any) => {
    setTimeRange(event.target.value);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: 2,
            boxShadow: theme.shadows[8],
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 'bold', 
              mb: 1,
              color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'
            }}
          >
            {formatDate(label)}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography 
              key={index} 
              variant="body2" 
              sx={{ 
                color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                fontWeight: 'medium'
              }}
            >
              <span style={{ color: entry.color, fontWeight: 'bold' }}>
                {entry.name}:
              </span> {entry.value}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Card
        elevation={theme.palette.dashboard?.gaugePanelBackgroundColor ? 0 : 1}
        sx={{
          mb: 4,
          borderRadius: 3,
          ...(theme.palette.dashboard?.gaugePanelBackgroundColor
            ? {
                bgcolor: theme.palette.dashboard.gaugePanelBackgroundColor,
                boxShadow: theme.palette.dashboard.gaugePanelBoxShadow ?? 3,
              }
            : {
                bgcolor: (t: any) =>
                  t.palette.mode === 'dark' ? 'rgb(20, 25, 55)' : t.palette.grey[100],
              }),
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card
        elevation={theme.palette.dashboard?.gaugePanelBackgroundColor ? 0 : 1}
        sx={{
          mb: 4,
          borderRadius: 3,
          ...(theme.palette.dashboard?.gaugePanelBackgroundColor
            ? {
                bgcolor: theme.palette.dashboard.gaugePanelBackgroundColor,
                boxShadow: theme.palette.dashboard.gaugePanelBoxShadow ?? 3,
              }
            : {
                bgcolor: (t: any) =>
                  t.palette.mode === 'dark' ? 'rgb(20, 25, 55)' : t.palette.grey[100],
              }),
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Alert severity="error">
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!analyticsData || analyticsData.timeSeriesData.length === 0) {
    return (
      <Card
        elevation={theme.palette.dashboard?.gaugePanelBackgroundColor ? 0 : 1}
        sx={{
          mb: 4,
          borderRadius: 3,
          ...(theme.palette.dashboard?.gaugePanelBackgroundColor
            ? {
                bgcolor: theme.palette.dashboard.gaugePanelBackgroundColor,
                boxShadow: theme.palette.dashboard.gaugePanelBoxShadow ?? 3,
              }
            : {
                bgcolor: (t: any) =>
                  t.palette.mode === 'dark' ? 'rgb(20, 25, 55)' : t.palette.grey[100],
              }),
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Campaign Analytics
          </Typography>
          <Typography variant="body2" color="textSecondary">
            No campaign data available for the selected time range.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const chartColors = {
    messages: theme.palette.mode === 'dark' ? '#4FC3F7' : '#1976D2', // Light blue for dark, darker blue for light
    completed: theme.palette.success.main,
    stopped: theme.palette.warning.main,
    cancelled: theme.palette.error.main,
  };

  const renderChart = () => {
    const commonProps = {
      data: analyticsData.timeSeriesData,
      margin: { top: 20, right: 30, left: 20, bottom: 5 },
    };

    switch (chartType) {
      case 'line':
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate}
              stroke={theme.palette.text.secondary}
            />
            <YAxis stroke={theme.palette.text.secondary} />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{
                color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'
              }}
            />
            <Line 
              type="monotone" 
              dataKey="messages" 
              stroke={chartColors.messages} 
              strokeWidth={2}
              dot={{ fill: chartColors.messages, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line 
              type="monotone" 
              dataKey="completed" 
              stroke={chartColors.completed} 
              strokeWidth={2}
              dot={{ fill: chartColors.completed, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line 
              type="monotone" 
              dataKey="stopped" 
              stroke={chartColors.stopped} 
              strokeWidth={2}
              dot={{ fill: chartColors.stopped, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line 
              type="monotone" 
              dataKey="cancelled" 
              stroke={chartColors.cancelled} 
              strokeWidth={2}
              dot={{ fill: chartColors.cancelled, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        );

      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate}
              stroke={theme.palette.text.secondary}
            />
            <YAxis stroke={theme.palette.text.secondary} />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{
                color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'
              }}
            />
            <Bar dataKey="messages" fill={chartColors.messages} />
            <Bar dataKey="completed" fill={chartColors.completed} />
            <Bar dataKey="stopped" fill={chartColors.stopped} />
            <Bar dataKey="cancelled" fill={chartColors.cancelled} />
          </BarChart>
        );

      case 'area':
        return (
          <AreaChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate}
              stroke={theme.palette.text.secondary}
            />
            <YAxis stroke={theme.palette.text.secondary} />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{
                color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'
              }}
            />
            <Area 
              type="monotone" 
              dataKey="messages" 
              stackId="1"
              stroke={chartColors.messages} 
              fill={chartColors.messages}
              fillOpacity={0.6}
            />
            <Area 
              type="monotone" 
              dataKey="completed" 
              stackId="1"
              stroke={chartColors.completed} 
              fill={chartColors.completed}
              fillOpacity={0.6}
            />
            <Area 
              type="monotone" 
              dataKey="stopped" 
              stackId="1"
              stroke={chartColors.stopped} 
              fill={chartColors.stopped}
              fillOpacity={0.6}
            />
            <Area 
              type="monotone" 
              dataKey="cancelled" 
              stackId="1"
              stroke={chartColors.cancelled} 
              fill={chartColors.cancelled}
              fillOpacity={0.6}
            />
          </AreaChart>
        );

      default:
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate}
              stroke={theme.palette.text.secondary}
            />
            <YAxis stroke={theme.palette.text.secondary} />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{
                color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'
              }}
            />
            <Line 
              type="monotone" 
              dataKey="messages" 
              stroke={chartColors.messages} 
              strokeWidth={2}
              dot={{ fill: chartColors.messages, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line 
              type="monotone" 
              dataKey="completed" 
              stroke={chartColors.completed} 
              strokeWidth={2}
              dot={{ fill: chartColors.completed, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line 
              type="monotone" 
              dataKey="stopped" 
              stroke={chartColors.stopped} 
              strokeWidth={2}
              dot={{ fill: chartColors.stopped, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line 
              type="monotone" 
              dataKey="cancelled" 
              stroke={chartColors.cancelled} 
              strokeWidth={2}
              dot={{ fill: chartColors.cancelled, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        );
    }
  };

  return (
    <Card
      elevation={theme.palette.dashboard?.gaugePanelBackgroundColor ? 0 : 1}
      sx={{
        mb: 4,
        borderRadius: 3,
        ...(theme.palette.dashboard?.gaugePanelBackgroundColor
          ? {
              bgcolor: theme.palette.dashboard.gaugePanelBackgroundColor,
              boxShadow: theme.palette.dashboard.gaugePanelBoxShadow ?? 3,
            }
          : {
              bgcolor: (t: any) =>
                t.palette.mode === 'dark' ? 'rgb(20, 25, 55)' : t.palette.grey[100],
            }),
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Campaign Analytics
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={handleTimeRangeChange}
              >
                <MenuItem value="7d">Last 7 Days</MenuItem>
                <MenuItem value="30d">Last 30 Days</MenuItem>
                <MenuItem value="90d">Last 90 Days</MenuItem>
              </Select>
            </FormControl>
            <ToggleButtonGroup
              value={chartType}
              exclusive
              onChange={handleChartTypeChange}
              size="small"
            >
              <ToggleButton value="line" aria-label="line chart">
                <TimelineIcon />
              </ToggleButton>
              <ToggleButton value="bar" aria-label="bar chart">
                <BarChartIcon />
              </ToggleButton>
              <ToggleButton value="area" aria-label="area chart">
                <TrendingUpIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Summary Stats */}
        <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
          <Chip 
            label={`${analyticsData.totalCampaigns} Campaigns`}
            sx={{
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.1)',
              color: theme.palette.mode === 'dark' ? '#90CAF9' : '#1976D2',
              borderColor: theme.palette.mode === 'dark' ? '#90CAF9' : '#1976D2',
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.3)' : 'rgba(25, 118, 210, 0.2)',
              }
            }}
            variant="outlined"
          />
          <Chip 
            label={`${analyticsData.totalMessages} Messages`}
            sx={{
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)',
              color: theme.palette.mode === 'dark' ? '#CE93D8' : '#9C27B0',
              borderColor: theme.palette.mode === 'dark' ? '#CE93D8' : '#9C27B0',
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(156, 39, 176, 0.3)' : 'rgba(156, 39, 176, 0.2)',
              }
            }}
            variant="outlined"
          />
          <Chip 
            label={`${analyticsData.statusBreakdown.completed} Completed`}
            sx={{
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)',
              color: theme.palette.mode === 'dark' ? '#A5D6A7' : '#4CAF50',
              borderColor: theme.palette.mode === 'dark' ? '#A5D6A7' : '#4CAF50',
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.2)',
              }
            }}
            variant="outlined"
          />
          <Chip 
            label={`${analyticsData.statusBreakdown.stopped} Stopped`}
            sx={{
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)',
              color: theme.palette.mode === 'dark' ? '#FFCC80' : '#FF9800',
              borderColor: theme.palette.mode === 'dark' ? '#FFCC80' : '#FF9800',
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.2)',
              }
            }}
            variant="outlined"
          />
          <Chip 
            label={`${analyticsData.statusBreakdown.cancelled} Cancelled`}
            sx={{
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.1)',
              color: theme.palette.mode === 'dark' ? '#EF9A9A' : '#F44336',
              borderColor: theme.palette.mode === 'dark' ? '#EF9A9A' : '#F44336',
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.2)',
              }
            }}
            variant="outlined"
          />
        </Box>

        {/* Chart */}
        <Box sx={{ height: 400, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
}; 